/**
 * ThemePreviewContext — test-drive de tema, escopado à ABA.
 *
 * A loja vende tema por um swatch de 72px: o usuário paga 400 moedas sem
 * nunca ter visto o tema aplicado de verdade. O preview resolve isso — ele
 * VESTE o tema no app inteiro antes de decidir.
 *
 * Escopo deliberado — `sessionStorage`, nem `localStorage` nem Firestore:
 *  · sobrevive a reload e a navegação (o teste vale a sessão de exploração);
 *  · morre quando a aba fecha, então nunca vira estado preso;
 *  · não viaja pra outra aba nem pro perfil público — `themePreference` e
 *    `themeId` no Firestore ficam INTOCADOS. Nada que o preview faz é
 *    visível pra outro usuário, e Settings continua mostrando o tema real.
 *
 * O preview ignora de propósito os guards de PRO e de coin-gate do
 * ThemeContext — testar o que você ainda NÃO tem é o ponto da feature. Isso
 * não abre furo: tema é cosmético, 100% client-side, o estado é temporário e
 * a `ThemePreviewBar` deixa explícito na tela que aquilo é um teste.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { THEMES } from "../config/themeRegistry";

/** Chave de aba. Convenção `livra_*` do projeto (ver postLoginTarget/scanSession). */
export const THEME_PREVIEW_STORAGE_KEY = "livra_theme_preview";

interface ThemePreviewContextData {
  /** Tema em teste, ou null quando não há preview ativo. */
  previewThemeId: string | null;
  startPreview: (themeId: string) => void;
  stopPreview: () => void;
}

const ThemePreviewContext = createContext<ThemePreviewContextData>({
  previewThemeId: null,
  startPreview: () => {},
  stopPreview: () => {},
});

/** Id precisa existir no registry — id inválido nunca vira `data-theme`. */
function isKnownTheme(themeId: string): boolean {
  return THEMES.some((t) => t.id === themeId);
}

function readStoredPreview(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(THEME_PREVIEW_STORAGE_KEY);
    return raw && isKnownTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function ThemePreviewProvider({ children }: { children: ReactNode }) {
  const [previewThemeId, setPreviewThemeId] = useState<string | null>(readStoredPreview);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (previewThemeId) {
        window.sessionStorage.setItem(THEME_PREVIEW_STORAGE_KEY, previewThemeId);
      } else {
        window.sessionStorage.removeItem(THEME_PREVIEW_STORAGE_KEY);
      }
    } catch {
      /* storage bloqueado (aba anônima) — o preview segue valendo em memória */
    }
  }, [previewThemeId]);

  const startPreview = useCallback((themeId: string) => {
    setPreviewThemeId(isKnownTheme(themeId) ? themeId : null);
  }, []);

  const stopPreview = useCallback(() => setPreviewThemeId(null), []);

  const value = useMemo<ThemePreviewContextData>(
    () => ({ previewThemeId, startPreview, stopPreview }),
    [previewThemeId, startPreview, stopPreview],
  );

  return (
    <ThemePreviewContext.Provider value={value}>
      {children}
    </ThemePreviewContext.Provider>
  );
}

export function useThemePreview(): ThemePreviewContextData {
  return useContext(ThemePreviewContext);
}
